/**
 * 経費申請の下書き(2026-08-26)。
 * 入力途中の内容を Firestore に保存し、申請一覧の「続きを入力」から再開できるようにする
 * (業務データはlocalStorage禁止のためFirestoreに保存)。申請の送信に成功したら削除する。添付ファイルは対象外(選び直し)
 *
 * 2026-09-17 改修依頼 A: 1人1件(drafts/{userId})では2件目の保存で1件目が黙って消えていたため、
 * 1件ずつ drafts/{userId}/items/{draftId} に保存する。旧形式の下書きも一覧に出し、保存し直すと新形式に移す。
 * 保存・削除の失敗は握りつぶさず、呼び出し側で画面に出す
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';

export type ExpenseDraft = {
  companyId: string;
  departmentId: string;
  locationId: string;
  accountId: string;
  subAccountId: string;
  postingDate: string;
  paymentDate: string;
  amountText: string;
  payer: string;
  paymentMethod: string;
  counterparty: string;
  description: string;
  note: string;
  /** 保存日時(申請一覧の下書き行に表示。Firestoreのタイムスタンプ) */
  updated_at?: unknown;
};

export type DraftItem = ExpenseDraft & { id: string };

/** 旧形式(1人1件・drafts/{userId} の文書そのもの)の下書きに付けるID */
export const LEGACY_DRAFT_ID = 'legacy';

const DRAFT_FIELDS = [
  'companyId',
  'departmentId',
  'locationId',
  'accountId',
  'subAccountId',
  'postingDate',
  'paymentDate',
  'amountText',
  'payer',
  'paymentMethod',
  'counterparty',
  'description',
  'note',
] as const;

function legacyRef(userId: string) {
  return doc(getDb(), 'drafts', userId);
}

function itemsRef(userId: string) {
  return collection(getDb(), 'drafts', userId, 'items');
}

function pickFields(src: Record<string, unknown>): ExpenseDraft {
  const out = {} as Record<string, string>;
  for (const f of DRAFT_FIELDS) out[f] = typeof src[f] === 'string' ? (src[f] as string) : '';
  return out as unknown as ExpenseDraft;
}

function millis(ts: unknown): number {
  const t = ts as { toMillis?: () => number } | null | undefined;
  return t?.toMillis ? t.toMillis() : 0;
}

/** 自分の下書き(新しい順)。読み込みに失敗したら例外を投げる(「下書きがない」と区別するため) */
export async function listDrafts(userId: string): Promise<DraftItem[]> {
  const [itemsSnap, legacySnap] = await Promise.all([getDocs(itemsRef(userId)), getDoc(legacyRef(userId))]);
  const list: DraftItem[] = itemsSnap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    return { ...pickFields(data), updated_at: data.updated_at, id: d.id };
  });
  if (legacySnap.exists()) {
    const data = legacySnap.data() as Record<string, unknown>;
    if (DRAFT_FIELDS.some((f) => f in data)) {
      list.push({ ...pickFields(data), updated_at: data.updated_at, id: LEGACY_DRAFT_ID });
    }
  }
  return list.sort((a, b) => millis(b.updated_at) - millis(a.updated_at));
}

/** 1件の下書き。見つからなければ null。読み込みに失敗したら例外 */
export async function loadDraft(userId: string, draftId: string): Promise<DraftItem | null> {
  if (draftId === LEGACY_DRAFT_ID) {
    const snap = await getDoc(legacyRef(userId));
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    return { ...pickFields(data), updated_at: data.updated_at, id: LEGACY_DRAFT_ID };
  }
  const snap = await getDoc(doc(itemsRef(userId), draftId));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  return { ...pickFields(data), updated_at: data.updated_at, id: snap.id };
}

/**
 * 下書きを保存する。draftId が null なら新しい下書きを作る。保存した下書きのIDを返す。
 * 旧形式の下書きを保存し直したときは新形式に移し、旧形式の文書を消す。失敗したら例外
 */
export async function saveDraft(userId: string, draftId: string | null, draft: ExpenseDraft): Promise<string> {
  const ref = draftId && draftId !== LEGACY_DRAFT_ID ? doc(itemsRef(userId), draftId) : doc(itemsRef(userId));
  await setDoc(ref, { ...pickFields(draft as unknown as Record<string, unknown>), updated_at: serverTimestamp() });
  if (draftId === LEGACY_DRAFT_ID) {
    // 移し終えた旧形式は消す(失敗しても新形式は保存済みのため、一覧に同じ内容が2行出るだけ)
    await deleteDoc(legacyRef(userId)).catch((e) => console.error('旧形式の下書きの削除に失敗しました', e));
  }
  return ref.id;
}

/** 下書きを削除する。失敗したら例外 */
export async function deleteDraft(userId: string, draftId: string): Promise<void> {
  await deleteDoc(draftId === LEGACY_DRAFT_ID ? legacyRef(userId) : doc(itemsRef(userId), draftId));
}
